// src/ForgotPassword.js
import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, Link as MuiLink } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import API_URL from './config';

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async event => {
        event.preventDefault();
        setError('');
        setSuccess('');
        if (!email) {
            setError('Email cannot be empty');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/users/forgot-password/`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (res.ok) {
                setSuccess('If that email is registered, you will receive a reset link shortly.');
            } else {
                // Handle validation errors
                if (data.non_field_errors) {
                    setError(data.non_field_errors[0] || 'Failed to send reset link');
                } else if (typeof data === 'object' && Object.keys(data).length > 0) {
                    // Handle field-level errors
                    const errorMsg = Object.values(data).flat().join(' ');
                    setError(errorMsg || 'Failed to send reset link');
                } else {
                    setError(data.detail || 'Failed to send reset link');
                }
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box 
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: '5vh',
                backgroundImage: 'linear-gradient(135deg, #1a237e 0%, #121858 100%)',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: '400px',
                    backgroundColor: '#fff',
                    padding: '40px',
                    borderRadius: '8px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box
                    component="img"
                    src="/logo.svg"
                    alt="Polfund Logo"
                    sx={{
                        width: 260,
                        marginBottom: 4,
                    }}
                />
                
                <Typography variant="h6" sx={{ mb: 3, color: '#333333' }}>
                    Zapomniałem hasła
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2, width: '100%' }}>
                        {success}
                    </Alert>
                )}

                {!success && (
                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            disabled={loading}
                            sx={{ mb: 3 }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            sx={{
                                mt: 1,
                                mb: 2,
                                padding: '12px',
                                backgroundColor: '#E31837',
                                '&:hover': {
                                    backgroundColor: '#FF1F3F',
                                },
                            }}
                        >
                            {loading ? 'Wysyłanie...' : 'Wyślij link resetujący'}
                        </Button>
                    </Box>
                )}

                <MuiLink
                    onClick={() => navigate('/login')}
                    sx={{
                        color: '#E31837',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    Powrót do logowania
                </MuiLink>
            </Box>
        </Box>
    );
};

export default ForgotPassword;
